import {
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'

import type { LibraryDocument, LibraryFolder } from '../../api/library'
import { asFailure, type Failure } from '../../api/request'
import {
  type IndexProgress,
  PAGE,
  type Reading as HowItWasRead,
  type SearchHit,
  type SearchOrder,
  type Typing,
  search,
} from '../../api/search'
import { isTask } from '../../api/search'
import { tickOffTask } from '../../api/tasks'
import { trailTo } from '../library/place'
import { Button } from '../../ui/Control'
import { Offer } from '../../ui/Offer'
import { Failed, Waiting } from '../../ui/Status'
import { useAnnounce } from '../../ui/Announce'
import { Hit } from './Hit'
import { type SearchAction, actionsFor } from './actions'
import { QueryField } from './QueryField'
import { Reading } from './Reading'
import { Action, Suggestions } from './Suggestions'

/**
 * The search itself: one field, one reading line, one list of hits.
 *
 * **There is one of these and it is used twice** — as the layer that opens over whatever somebody was
 * doing, and as the area behind it. Not two searches that would answer differently within a month: the
 * same component in two frames, with a switch for how much room it has. Everything that is a rule of the
 * search rather than of the frame lives here, which is why the frames themselves are short.
 *
 * Three of those rules are worth reading twice.
 *
 * **Suggestions replace the hits, and only while the cursor stands in a filter token.** The reason is the
 * keyboard: the arrow keys have to move exactly one list, and two lists on screen at once would need a
 * rule about which — which is the kind of ambiguity that makes a keyboard interface feel broken. And while
 * `folder:Sem` is half typed the question is half asked, so hits for it are hits for something nobody is
 * looking for.
 *
 * **The list is the whole result and it scrolls.** Not the first five with a "see all" underneath: the
 * arrow keys walk through everything, another page is fetched when the end comes near, and there is a
 * visible control at the end all the same — for the keyboard, for a screen reader, and for anybody whose
 * scrolling did not trigger anything.
 *
 * **Nothing here answers with an error.** A line MyLib did not understand is search text, marked in the
 * field and explained in the reading line. Only a service that cannot be reached is a failure.
 */
export type Shape =
  /** Over everything else, on a small surface: the layer. */
  | 'compact'
  /** In an area of its own, with room for the filters and the state of the reading. */
  | 'full'

export interface SearchingProps {
  readonly shape: Shape
  readonly line: string
  readonly order: SearchOrder
  /** Called on every keystroke. Whoever owns the line decides what to do with it — including the address. */
  readonly onLine: (line: string) => void
  readonly onOpen: (hit: SearchHit) => void
  /** Escape, where there is somewhere to go back to. Only the layer has one. */
  readonly onLeave?: () => void
  /** The folders, so a hit can say where it lives. Empty until they have been fetched. */
  readonly folders: readonly LibraryFolder[]
  /** Put under the reading line: the filters and the sort order, in the full shape. */
  readonly controls?: ReactNode
  /** Put at the very bottom: the way into the detailed search, or the rebuild control. */
  readonly footer?: ReactNode
  /** Whether the field takes the focus when it appears. The layer's whole reason for existing. */
  readonly focused?: boolean
  /**
   * What came back with the last answer: how the line was read, and how far the space has been read.
   *
   * The frame around this needs both — the filter controls put their tokens where the reading says the
   * old ones stand, and the progress control shows the counts. Handing them out is cheaper than a second
   * request, and they are the same numbers the reading line is already showing.
   */
  readonly onRead?: (reading: HowItWasRead, progress: IndexProgress) => void
}

/** How long after the last keystroke the question is actually asked. */
export const TYPING_PAUSE_MS = 300

/** One empty list, so that "nothing asked yet" does not make a new array on every render. */
const NOTHING: readonly SearchHit[] = []
const NO_ACTIONS: readonly SearchAction[] = []

export function Searching({
  shape,
  line,
  order,
  onLine,
  onOpen,
  onLeave,
  folders,
  controls,
  footer,
  focused = false,
  onRead,
}: SearchingProps) {
  const field = useRef<HTMLInputElement>(null)
  const listId = useId()
  const announce = useAnnounce()

  const [caret, setCaret] = useState(line.length)
  const [hits, setHits] = useState<readonly SearchHit[]>([])
  const [total, setTotal] = useState(0)
  const [read, setRead] = useState<HowItWasRead | null>(null)
  const [typing, setTyping] = useState<Typing | null>(null)
  const [progress, setProgress] = useState<IndexProgress | null>(null)
  const [failure, setFailure] = useState<Failure | null>(null)
  const [busy, setBusy] = useState(false)
  const [active, setActive] = useState(0)

  /** What the hits on screen answer. A cursor moving does not make them stale; a new line does. */
  const answered = useRef({ line: '', order })
  /** How many hits are on screen, where an answer has to know without depending on them. */
  const howMany = useRef(0)
  /** Where the cursor belongs after a suggestion was taken. Written to the field, never to state. */
  const putCaretAt = useRef<number | null>(null)

  useEffect(() => {
    if (focused) {
      field.current?.focus()
    }
  }, [focused])

  /*
   * The cursor goes where a taken suggestion left it, once React has written the new line into the field.
   *
   * A ref and not state: this effect only moves something in the DOM, which is exactly what an effect is
   * for. Making it state would set state from an effect, which cascades renders for nothing.
   */
  useEffect(() => {
    const at = putCaretAt.current
    if (at === null) {
      return
    }
    putCaretAt.current = null
    field.current?.setSelectionRange(at, at)
  })

  const ask = useCallback(
    async (signal: AbortSignal) => {
      const answer = await search({ line, caret, order, limit: PAGE, offset: 0 }, signal)
      if (signal.aborted) {
        return
      }
      setRead(answer.reading)
      setTyping(answer.typing)
      setProgress(answer.index)
      onRead?.(answer.reading, answer.index)
      setFailure(null)

      /*
       * A cursor moving asks again — for the suggestions — and must not disturb what is on screen.
       * A new line, or a new order, is a different question and starts at the top.
       *
       * **The same question never shortens the list.** Asking again answers with the first page, and
       * replacing fifty hits with twenty-five pulls the list out from under whoever was reading it:
       * the row under the pointer becomes a different row, and the next click opens something else.
       * So the answer to a question already on screen refreshes what it covers and leaves the pages
       * fetched since exactly where they are.
       */
      const sameQuestion = answered.current.line === line && answered.current.order === order
      setTotal(answer.total)
      if (!sameQuestion) {
        setHits(answer.hits)
        // A different question, so whatever the keyboard was on is gone with the old answer.
        setActive(0)
        howMany.current = answer.hits.length
      } else {
        setHits((shown) => {
          const kept = shown.length > answer.hits.length ? shown.slice(answer.hits.length) : []
          const together = [...answer.hits, ...kept]
          howMany.current = together.length
          return together
        })
      }
      answered.current = { line, order }
    },
    // The line, the cursor and the order — the three things a question is made of. Read straight out of
    // the props and the state, never out of an object built here, or this would change on every render.
    [line, caret, order, onRead],
  )

  useEffect(() => {
    // An empty line is not asked at all. What is left over in the state is simply not shown — see the
    // derived values below — so there is nothing to clear, and clearing it would mean setting state from
    // inside an effect for no visible difference.
    if (line.trim() === '') {
      return
    }

    const controller = new AbortController()
    const timer = setTimeout(() => {
      setBusy(true)
      ask(controller.signal)
        .catch((error: unknown) => {
          if (!controller.signal.aborted) {
            setFailure(asFailure(error))
          }
        })
        .finally(() => {
          if (!controller.signal.aborted) {
            setBusy(false)
          }
        })
    }, TYPING_PAUSE_MS)

    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [ask, line])

  const loadMore = async () => {
    try {
      setBusy(true)
      const answer = await search({ line, order, limit: PAGE, offset: hits.length })
      // Appended, always, and **`active` is left exactly where it was**: another page arriving is not
      // a reason for the keyboard to jump, and it is certainly not a reason for the row somebody was
      // about to click to become a different row.
      const together = [...hits, ...answer.hits]
      setHits(together)
      setTotal(answer.total)
      howMany.current = together.length
      announce(`${answer.hits.length} more hits, at the end of the list.`)
    } catch (error) {
      setFailure(asFailure(error))
    } finally {
      setBusy(false)
    }
  }

  /**
   * Ticks the task a hit points at off, or takes that back, **without leaving the results**.
   *
   * The one thing in a result list that does something rather than leading somewhere, and it earns
   * that: a search over open points is very often the question "have I still got to do that?", and
   * the answer to it is frequently "no — and now it is done". Making somebody open the list to say
   * so would be a step for its own sake.
   *
   * The hit is written back into the list rather than the whole search being asked again. Asking
   * again would rebuild the list under whoever is reading it, and a task that has just been ticked
   * off must not jump or disappear — it is still a hit, and taking it back has to stay one control
   * away.
   */
  const tickOff = async (hit: SearchHit, done: boolean) => {
    const task = hit.task
    if (task === null || task === undefined) {
      return
    }
    try {
      const changed = await tickOffTask(task.id, done)
      setHits((shown) =>
        shown.map((one) => (one.task?.id === changed.id ? { ...one, task: changed } : one)),
      )
      setFailure(null)
      announce(done ? 'Ticked off.' : 'Open again.')
    } catch (error) {
      setFailure(asFailure(error))
    }
  }

  /*
   * Nothing has been asked, so nothing left over from the last question is shown. Derived rather than
   * cleared: what is in the state is invisible until there is a line again, and the answer to that line
   * replaces it.
   */
  const nothingAsked = line.trim() === ''
  const shownRead = nothingAsked ? null : read
  const shownTyping = nothingAsked ? null : typing
  const shownHits = nothingAsked ? NOTHING : hits
  const shownFailure = nothingAsked ? null : failure

  const suggesting = shownTyping !== null && shownTyping.suggestions.length > 0

  /*
   * What the search can *do* with this line, and it is walked with the arrow keys like everything
   * else. It used to be drawn above the hits and be reachable by the mouse alone — which made it the
   * one thing on this screen that the keyboard could not get to, on the one surface that is opened
   * with a key combination.
   */
  const actions = suggesting ? NO_ACTIONS : actionsFor(line)

  const walking = suggesting ? shownTyping.suggestions.length : actions.length + shownHits.length
  const more = shownHits.length < total

  const take = (value: string) => {
    if (shownTyping === null) {
      return
    }
    const written = `${line.slice(0, shownTyping.from)}${value}${line.slice(shownTyping.to)}`
    const at = shownTyping.from + value.length
    onLine(written)
    setCaret(at)
    setTyping(null)
    putCaretAt.current = at
  }

  const ghost = ghostFor(line, caret, shownTyping)

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape' && onLeave !== undefined) {
      event.preventDefault()
      onLeave()
      return
    }
    if (event.key === 'Tab' && ghost !== null) {
      // The ghost completion, taken. Tab and not Enter: Enter opens what is selected, everywhere.
      event.preventDefault()
      const at = line.length + ghost.length
      onLine(`${line}${ghost}`)
      setCaret(at)
      putCaretAt.current = at
      return
    }
    if (event.key === 'ArrowDown' && walking > 0) {
      event.preventDefault()
      setActive((at) => (at + 1) % walking)
      return
    }
    if (event.key === 'ArrowUp' && walking > 0) {
      event.preventDefault()
      setActive((at) => (at - 1 + walking) % walking)
      return
    }
    if (event.key === 'Enter') {
      event.preventDefault()
      if (suggesting) {
        const chosen = shownTyping.suggestions[active]
        if (chosen !== undefined) {
          take(chosen.value)
        }
        return
      }
      // The actions stand at the top of the list, so the first of them are what the first indices
      // mean, and a hit is whatever is left after them.
      const action = actions[active]
      if (action !== undefined) {
        void action.run().then(() => {
          onLeave?.()
        })
        return
      }
      const chosen = shownHits[active - actions.length]
      if (chosen === undefined) {
        return
      }
      /*
       * Shift and Enter ticks a task off where it stands; Enter alone opens it.
       *
       * The shortcut exists because the hands are already on the keyboard — the arrow keys are what
       * walked here — and it is never the only way: the box on the row does the same with a finger
       * or a pointer, and it is shown beside the row the keyboard is on so that the key is
       * discoverable rather than folklore. On anything that is not a task, Shift changes nothing.
       */
      if (event.shiftKey && isTask(chosen)) {
        void tickOff(chosen, chosen.task?.doneAt === null)
        return
      }
      onOpen(chosen)
    }
  }

  const activeId = walking === 0 ? undefined : `${listId}-${active}`

  return (
    <div className="searching" data-shape={shape}>
      <QueryField
        line={line}
        reading={shownRead}
        ghost={ghost}
        placeholder="Search everything you keep"
        label="Search"
        inputRef={field}
        listId={listId}
        activeId={activeId}
        onChange={(written, at) => {
          onLine(written)
          setCaret(at)
        }}
        onCaret={setCaret}
        onKeyDown={onKeyDown}
      />

      <Reading reading={shownRead} progress={progress} />

      {controls}

      {shownFailure !== null && <Failed failure={shownFailure} />}

      {suggesting ? (
        <Suggestions
          listId={listId}
          suggestions={shownTyping.suggestions}
          active={active}
          onTake={take}
        />
      ) : (
        <Hits
          listId={listId}
          actions={actions}
          onRun={(action) => {
            void action.run().then(() => {
              onLeave?.()
            })
          }}
          hits={shownHits}
          folders={folders}
          active={active}
          line={line}
          total={total}
          busy={busy}
          more={more}
          onOpen={onOpen}
          onTickOff={(hit, done) => {
            void tickOff(hit, done)
          }}
          onMore={() => {
            void loadMore()
          }}
        />
      )}

      {footer}
    </div>
  )
}

interface HitsProps {
  readonly listId: string
  /** What the search can *do* with this line. They stand first, and are walked like a hit. */
  readonly actions: readonly SearchAction[]
  readonly onRun: (action: SearchAction) => void
  readonly hits: readonly SearchHit[]
  readonly folders: readonly LibraryFolder[]
  readonly active: number
  readonly line: string
  readonly total: number
  readonly busy: boolean
  readonly more: boolean
  readonly onOpen: (hit: SearchHit) => void
  /** Ticks off the task a hit points at. Handed on only for a hit that is one. */
  readonly onTickOff: (hit: SearchHit, done: boolean) => void
  readonly onMore: () => void
}

/**
 * The hits, and what stands in their place when there are none.
 *
 * Another page is fetched when the end comes into view, **and** there is a control at the end that does
 * the same thing. Not one or the other: scrolling is faster, and a control is the only one of the two that
 * a keyboard, a screen reader and a person on a slow connection can rely on.
 */
function Hits({
  listId,
  actions,
  onRun,
  hits,
  folders,
  active,
  line,
  total,
  busy,
  more,
  onOpen,
  onTickOff,
  onMore,
}: HitsProps) {
  const end = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const sentinel = end.current
    // Where there is no such thing to watch with, the visible control at the end of the list does the
    // same job — which is one of the reasons it is there and not only a convenience.
    if (sentinel === null || !more || busy || typeof IntersectionObserver === 'undefined') {
      return
    }
    const watching = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        onMore()
      }
    })
    watching.observe(sentinel)
    return () => {
      watching.disconnect()
    }
  }, [more, busy, onMore])

  if (line.trim() === '') {
    return (
      <Offer
        title="Look inside everything you keep"
        lead="A word you half remember, looked for in the text of your documents rather than in their names — the scripts from a lecture, a handout, a photograph of a whiteboard once its text has been read."
        points={[
          'Quotation marks for an exact phrase, a minus to leave something out',
          'folder:“Semester 3” or created:2025-11 to narrow it down',
        ]}
      />
    )
  }

  if (hits.length === 0 && actions.length === 0) {
    return busy ? (
      <Waiting label="Looking." />
    ) : (
      <Offer
        title="Nothing found"
        lead="Fewer words find more. A word in a different form is no problem — a filter that is too narrow usually is, so try letting one go."
      />
    )
  }

  return (
    <>
      {/*
        One list, and the actions are the first entries in it. Two lists side by side would need a
        rule about which of them the arrow keys move — and the answer to that was, until now, "the
        other one", which is why the action could not be reached from the keyboard at all.
      */}
      <ul className="hits" role="listbox" id={listId} aria-label={`${total} hits`}>
        {actions.map((action, index) => (
          <Action
            key={action.id}
            id={`${listId}-${index}`}
            action={action}
            active={index === active}
            onRun={() => {
              onRun(action)
            }}
          />
        ))}
        {hits.map((hit, index) => (
          <Hit
            key={`${hit.kind}-${hit.document?.id ?? hit.note?.id ?? hit.task?.id ?? index}`}
            id={`${listId}-${actions.length + index}`}
            hit={hit}
            folder={
              hit.document === null || hit.document === undefined
                ? ''
                : folderOf(hit.document, folders)
            }
            active={actions.length + index === active}
            onOpen={() => {
              onOpen(hit)
            }}
            onTickOff={
              isTask(hit)
                ? (done) => {
                    onTickOff(hit, done)
                  }
                : undefined
            }
          />
        ))}
      </ul>

      <div className="hits__end" ref={end}>
        {/*
          The control **stays** while the page it asked for is on its way.

          It used to be swapped for a waiting line, which took the focus with it: whoever had just
          pressed it was left focused on nothing at all, so the next key went to the page rather than
          to the list. It says what it is doing instead, and ignores a second press.
        */}
        {more && (
          <Button
            tone="quiet"
            aria-busy={busy}
            onClick={() => {
              if (!busy) {
                onMore()
              }
            }}
          >
            {busy ? 'Fetching more hits…' : `More hits (${total - hits.length} of ${total} left)`}
          </Button>
        )}
        {busy && !more && <Waiting label="Fetching more hits." />}
      </div>
    </>
  )
}

/** Where a document lives, spelled out — the same path a folder is offered under in the library. */
function folderOf(document: LibraryDocument, folders: readonly LibraryFolder[]): string {
  if (document.folderId === null) {
    return ''
  }
  return trailTo({ kind: 'folder', id: document.folderId }, folders)
    .map((step) => step.name)
    .join(' / ')
}

/**
 * The rest of a filter key, shown grey behind the cursor.
 *
 * Only at the very end of the line, and only while the key itself is being typed: a completion in the
 * middle of a line would have to push characters sideways, and the field would jump about as somebody
 * types.
 */
function ghostFor(line: string, caret: number, typing: Typing | null): string | null {
  if (
    typing === null ||
    typing.key !== null ||
    typing.to !== line.length ||
    caret !== line.length
  ) {
    return null
  }
  const first = typing.suggestions[0]
  if (first === undefined) {
    return null
  }
  const typed = line.slice(typing.from, typing.to)
  return first.value.toLowerCase().startsWith(typed.toLowerCase())
    ? first.value.slice(typed.length)
    : null
}
